import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Copy, RefreshCw, Save, Send, Undo2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAdminApi } from '@app/api/api-client-context'
import { useAuth } from '@app/auth/auth-context'
import {
	emptyExerciseValues,
	exerciseKeys,
	exerciseToFormValues,
	formValuesToMutation,
	lifecycleLabel,
	type ExerciseFormValues,
	type ExerciseTaxonomies,
	type ValidationReport,
} from '@entities/exercise'
import { AdminApiError, createIdempotencyKey, unwrapApiResponse } from '@shared/api'
import { getInterfaceErrorDescription } from '@shared/i18n/interface-messages'
import { ConfirmDialog } from '@shared/ui/confirm-dialog'
import { InlineNotice, LoadingState } from '@shared/ui/page'
import { useToast } from '@shared/ui/toast'
import { ExerciseEditorFields } from './editor-fields'

type LifecycleAction = 'publish' | 'unpublish' | 'archive' | 'restore'

const actionMeta: Record<LifecycleAction, { title: string; description: string; label: string }> = {
	publish: { title: 'Опубликовать упражнение?', description: 'Упражнение станет доступно потребителям после серверной проверки.', label: 'Опубликовать' },
	unpublish: { title: 'Снять упражнение с публикации?', description: 'Упражнение вернётся в состояние черновика.', label: 'Снять с публикации' },
	archive: { title: 'Архивировать упражнение?', description: 'Упражнение исчезнет из активного каталога, но останется доступно для восстановления.', label: 'Архивировать' },
	restore: { title: 'Восстановить упражнение?', description: 'Упражнение вернётся в состояние черновика.', label: 'Восстановить' },
}

export const ExerciseEditor = ({ open, exerciseId, taxonomies, onClose }: {
	open: boolean
	exerciseId: string | null
	taxonomies: ExerciseTaxonomies
	onClose: () => void
}) => {
	const api = useAdminApi()
	const queryClient = useQueryClient()
	const { can } = useAuth()
	const { notify } = useToast()
	const [etag, setEtag] = useState<string | null>(null)
	const [pending, setPending] = useState<string | null>(null)
	const [validation, setValidation] = useState<ValidationReport | null>(null)
	const [conflict, setConflict] = useState(false)
	const [closeConfirmation, setCloseConfirmation] = useState(false)
	const [lifecycleAction, setLifecycleAction] = useState<LifecycleAction | null>(null)
	const [cloneOpen, setCloneOpen] = useState(false)
	const [cloneName, setCloneName] = useState('')
	const form = useForm<ExerciseFormValues>({ defaultValues: emptyExerciseValues(taxonomies) })
	const values = form.watch()

	const detailQuery = useQuery({
		queryKey: exerciseKeys.detail(exerciseId ?? 'new'),
		enabled: open && Boolean(exerciseId),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/exercises/{exercise_id}', {
			params: { path: { exercise_id: exerciseId! } },
		})),
	})
	const currentExercise = detailQuery.data?.data ?? null
	const editable = exerciseId
		? currentExercise?.lifecycle === 'DRAFT' && can('updateExercise')
		: can('createExercise')
	const canSave = editable && !pending && values.name.trim().length > 0

	useEffect(() => {
		if (!open) return
		setValidation(null)
		setConflict(false)
		if (!exerciseId) {
			form.reset(emptyExerciseValues(taxonomies))
			setEtag(null)
		}
	}, [open, exerciseId, form, taxonomies])

	useEffect(() => {
		if (!detailQuery.data) return
		form.reset(exerciseToFormValues(detailQuery.data.data))
		setEtag(detailQuery.data.etag ?? detailQuery.data.data.etag)
		setCloneName(`${detailQuery.data.data.name} — копия`)
	}, [detailQuery.data, form])

	useEffect(() => {
		if (!form.formState.isDirty) return
		const listener = (event: BeforeUnloadEvent) => {
			event.preventDefault()
			event.returnValue = ''
		}
		window.addEventListener('beforeunload', listener)
		return () => window.removeEventListener('beforeunload', listener)
	}, [form.formState.isDirty])

	const invalidate = useCallback(async (id?: string) => {
		await queryClient.invalidateQueries({ queryKey: exerciseKeys.all })
		if (id) await queryClient.invalidateQueries({ queryKey: exerciseKeys.detail(id) })
	}, [queryClient])

	const showError = (error: unknown, title: string) => {
		notify({ title, description: getInterfaceErrorDescription(error), tone: 'error' })
	}

	const save = form.handleSubmit(async formValues => {
		setPending('save')
		setConflict(false)
		try {
			const body = formValuesToMutation(formValues)
			if (!exerciseId) {
				const result = unwrapApiResponse(await api.POST('/admin/v1/exercises', {
					params: { header: { 'Idempotency-Key': createIdempotencyKey() } },
					body,
				}))
				await invalidate(result.data.id)
				notify({ title: 'Упражнение создано', description: result.data.name, tone: 'success' })
				onClose()
			} else {
				const result = unwrapApiResponse(await api.PATCH('/admin/v1/exercises/{exercise_id}', {
					params: {
						path: { exercise_id: exerciseId },
						header: { 'If-Match': etag ?? currentExercise?.etag ?? '' },
					},
					body,
				}))
				form.reset(exerciseToFormValues(result.data))
				setEtag(result.etag ?? result.data.etag)
				await invalidate(exerciseId)
				notify({ title: 'Изменения сохранены', description: result.data.name, tone: 'success' })
			}
		} catch (error) {
			if (error instanceof AdminApiError && error.status === 409 && exerciseId) {
				setConflict(true)
				try {
					const latest = unwrapApiResponse(await api.GET('/admin/v1/exercises/{exercise_id}', {
						params: { path: { exercise_id: exerciseId } },
					}))
					setEtag(latest.etag ?? latest.data.etag)
				} catch {
					// Keep the local draft and original conflict visible.
				}
				notify({
					title: 'Конфликт версии',
					description: 'На сервере есть более новая версия. Локальный черновик сохранён; проверьте его и повторите сохранение.',
					tone: 'error',
				})
			} else showError(error, 'Не удалось сохранить упражнение')
		} finally {
			setPending(null)
		}
	})

	const validate = async () => {
		if (!exerciseId) return
		setPending('validate')
		try {
			const result = unwrapApiResponse(await api.POST('/admin/v1/exercises/{exercise_id}/validate', {
				params: { path: { exercise_id: exerciseId } },
			}))
			setValidation(result.data)
			notify({
				title: result.data.valid ? 'Проверка пройдена' : 'Найдены ошибки',
				description: result.data.valid ? 'Упражнение готово к публикации.' : `${result.data.issues.length} замечаний`,
				tone: result.data.valid ? 'success' : 'error',
			})
		} catch (error) {
			showError(error, 'Не удалось проверить упражнение')
		} finally {
			setPending(null)
		}
	}

	const runLifecycle = async (action: LifecycleAction) => {
		if (!exerciseId) return
		setPending(action)
		try {
			const options = {
				params: {
					path: { exercise_id: exerciseId },
					header: {
						'Idempotency-Key': createIdempotencyKey(),
						'If-Match': etag ?? currentExercise?.etag ?? '',
					},
				},
			}
			const response = action === 'publish'
				? await api.POST('/admin/v1/exercises/{exercise_id}/publish', options)
				: action === 'unpublish'
					? await api.POST('/admin/v1/exercises/{exercise_id}/unpublish', options)
					: action === 'archive'
						? await api.POST('/admin/v1/exercises/{exercise_id}/archive', options)
						: await api.POST('/admin/v1/exercises/{exercise_id}/restore', options)
			const result = unwrapApiResponse(response)
			form.reset(exerciseToFormValues(result.data))
			setEtag(result.etag ?? result.data.etag)
			setValidation(null)
			await invalidate(exerciseId)
			notify({ title: actionMeta[action].label, description: result.data.name, tone: 'success' })
		} catch (error) {
			showError(error, `Не удалось выполнить действие «${actionMeta[action].label}»`)
		} finally {
			setPending(null)
			setLifecycleAction(null)
		}
	}

	const clone = async () => {
		if (!exerciseId || !cloneName.trim()) return
		setPending('clone')
		try {
			const result = unwrapApiResponse(await api.POST('/admin/v1/exercises/{exercise_id}/clone', {
				params: {
					path: { exercise_id: exerciseId },
					header: { 'Idempotency-Key': createIdempotencyKey() },
				},
				body: { name: cloneName.trim() },
			}))
			await invalidate(result.data.id)
			setCloneOpen(false)
			notify({ title: 'Копия создана', description: result.data.name, tone: 'success' })
		} catch (error) {
			showError(error, 'Не удалось клонировать упражнение')
		} finally {
			setPending(null)
		}
	}

	const requestClose = (nextOpen: boolean) => {
		if (nextOpen) return
		if (form.formState.isDirty) setCloseConfirmation(true)
		else onClose()
	}

	const lifecycleButton = useMemo(() => {
		if (!currentExercise) return null
		if (currentExercise.lifecycle === 'DRAFT' && can('publishExercise')) return (
			<Button type='button' variant='outline' onClick={() => setLifecycleAction('publish')} disabled={Boolean(pending)}><Send className='h-4 w-4' /> Опубликовать</Button>
		)
		if (currentExercise.lifecycle === 'PUBLISHED' && can('unpublishExercise')) return (
			<Button type='button' variant='outline' onClick={() => setLifecycleAction('unpublish')} disabled={Boolean(pending)}><Undo2 className='h-4 w-4' /> Снять с публикации</Button>
		)
		if (currentExercise.lifecycle === 'ARCHIVED' && can('restoreExercise')) return (
			<Button type='button' variant='outline' onClick={() => setLifecycleAction('restore')} disabled={Boolean(pending)}><RefreshCw className='h-4 w-4' /> Восстановить</Button>
		)
		return null
	}, [can, currentExercise, pending])

	return (
		<>
			<Dialog open={open} onOpenChange={requestClose}>
				<DialogContent className='max-h-[94vh] max-w-6xl overflow-y-auto p-0'>
					<DialogHeader className='sticky top-0 z-10 border-b bg-background px-6 py-5 pr-12'>
						<DialogTitle>{exerciseId ? 'Редактирование упражнения' : 'Новое упражнение'}</DialogTitle>
						<DialogDescription>
							{currentExercise ? `${lifecycleLabel[currentExercise.lifecycle]} · версия ${currentExercise.version}` : 'Создание административного черновика'}
						</DialogDescription>
					</DialogHeader>
					{detailQuery.isPending && exerciseId ? <div className='p-6'><LoadingState label='Загрузка упражнения…' /></div> : (
						<form onSubmit={save} className='space-y-6 p-6'>
							{conflict ? <InlineNotice title='Обнаружен конфликт версии' description='Серверная версия изменилась. Локальный черновик не сброшен; проверьте значения и повторите сохранение с новым ETag.' tone='warning' /> : null}
							<ExerciseEditorFields form={form} taxonomies={taxonomies} editable={editable} validation={validation} />
							<DialogFooter className='sticky bottom-0 -mx-6 -mb-6 border-t bg-background px-6 py-4 sm:justify-between sm:space-x-0'>
								<div className='flex flex-wrap gap-2'>
									{exerciseId && can('validateExercise') ? <Button type='button' variant='outline' onClick={() => void validate()} disabled={Boolean(pending)}>Проверить</Button> : null}
									{exerciseId && can('cloneExercise') ? <Button type='button' variant='outline' onClick={() => setCloneOpen(true)} disabled={Boolean(pending)}><Copy className='h-4 w-4' /> Клонировать</Button> : null}
									{lifecycleButton}
									{currentExercise?.lifecycle !== 'ARCHIVED' && exerciseId && can('archiveExercise') ? <Button type='button' variant='outline' onClick={() => setLifecycleAction('archive')} disabled={Boolean(pending)}><Archive className='h-4 w-4' /> В архив</Button> : null}
								</div>
								<div className='flex gap-2'>
									<Button type='button' variant='outline' onClick={() => requestClose(false)}>Закрыть</Button>
									{editable ? <Button type='submit' disabled={!canSave}><Save className='h-4 w-4' /> {pending === 'save' ? 'Сохранение…' : 'Сохранить'}</Button> : null}
								</div>
							</DialogFooter>
						</form>
					)}
				</DialogContent>
			</Dialog>

			<ConfirmDialog open={closeConfirmation} onOpenChange={setCloseConfirmation} title='Закрыть без сохранения?' description='Все несохранённые изменения будут потеряны.' confirmLabel='Закрыть' destructive onConfirm={() => { setCloseConfirmation(false); onClose() }} />
			<ConfirmDialog open={Boolean(lifecycleAction)} onOpenChange={value => { if (!value) setLifecycleAction(null) }} title={lifecycleAction ? actionMeta[lifecycleAction].title : ''} description={lifecycleAction ? actionMeta[lifecycleAction].description : ''} confirmLabel={lifecycleAction ? actionMeta[lifecycleAction].label : ''} destructive={lifecycleAction === 'archive'} isPending={Boolean(pending)} onConfirm={() => lifecycleAction ? runLifecycle(lifecycleAction) : undefined} />
			<ConfirmDialog open={cloneOpen} onOpenChange={setCloneOpen} title='Создать копию упражнения' description='Копия будет создана как отдельный черновик.' confirmLabel='Клонировать' isPending={pending === 'clone'} onConfirm={clone}>
				<div><Label htmlFor='clone-name'>Название копии</Label><Input id='clone-name' value={cloneName} onChange={event => setCloneName(event.target.value)} className='mt-2' /></div>
			</ConfirmDialog>
		</>
	)
}
